/**
 * Price History List component for displaying price history
 * Shows table format on desktop (≥768px) and card format on mobile (<768px)
 */

import { formatVND } from '@/utils/currencyUtils'
import { formatLocalDate } from '@/utils/dateUtils'
import { PriceHistoryCard } from './PriceHistoryCard'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { IconHistory } from '@tabler/icons-react'
import type { GoldPriceHistory } from '@/types/price'

interface PriceHistoryListProps {
  prices: GoldPriceHistory[]
  isLoading?: boolean
}

export function PriceHistoryList({ prices, isLoading }: PriceHistoryListProps) {
  if (isLoading) {
    // Mobile skeleton
    const mobileSkeleton = (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className="p-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-20" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    )
    
    // Desktop skeleton
    const desktopSkeleton = (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
    
    return (
      <>
        <div className="block md:hidden">{mobileSkeleton}</div>
        <div className="hidden md:block">{desktopSkeleton}</div>
      </>
    )
  }

  if (prices.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconHistory className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Không có dữ liệu giá</EmptyTitle>
              <EmptyDescription>
                Hãy thử thay đổi bộ lọc hoặc thu thập giá mới
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  // Group by date to show gaps
  const groupedByDate = prices.reduce((acc, price) => {
    const date = price.date
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date]!.push(price)
    return acc
  }, {} as Record<string, GoldPriceHistory[]>)

  const sortedDates = Object.keys(groupedByDate).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )

  // Mobile card view
  const mobileView = (
    <div className="space-y-3">
      {sortedDates.map((date) =>
        groupedByDate[date]?.map((price) => (
          <PriceHistoryCard key={price.id} price={price} />
        ))
      )}
    </div>
  )

  // Desktop table view
  const desktopView = (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ngày</TableHead>
                <TableHead>Loại vàng</TableHead>
                <TableHead>Chi nhánh</TableHead>
                <TableHead>Giá mua</TableHead>
                <TableHead>Giá bán</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDates.map((date) =>
                groupedByDate[date]?.map((price) => (
                  <TableRow key={price.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatLocalDate(price.date)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {price.goldTypeId}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {price.branchName}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatVND(price.buyPrice)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-semibold text-primary">
                      {formatVND(price.sellPrice)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <>
      <div className="block md:hidden">{mobileView}</div>
      <div className="hidden md:block">{desktopView}</div>
    </>
  )
}

